import { Button, Card, CardBody, CardHeader, CardMeta, CardTitle } from "@community-project-org/ui";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { apiRequest } from "../../common/api/apiClient.js";
import { QrCode } from "../../common/components/QrCode.js";
import type { components } from "../../generated/api.js";

type MyPassDTO = components["schemas"]["MyPassDTO"];
type MyAccessDTO = components["schemas"]["MyAccessDTO"];

/**
 * Die eigenen Mitgliedsausweise mit rotierendem QR-Code. Der Code wechselt alle 30
 * Sekunden; die App holt ihn rechtzeitig neu, damit ein weitergegebener Screenshot
 * nach kurzer Zeit nichts mehr öffnet.
 */
export function MemberPasses(): JSX.Element | null {
  const passes = useQuery({
    queryKey: ["my-member-passes"],
    queryFn: () => apiRequest<MyPassDTO[]>("/my/member-passes", { tenant: null }),
    // Kurz vor dem Wechsel neu laden; ohne Ausweis gar nicht.
    refetchInterval: (query) => {
      const first = query.state.data?.[0];
      return first ? Math.max(1, first.refreshInSeconds) * 1000 : false;
    },
    refetchIntervalInBackground: false,
  });

  if (!passes.data || passes.data.length === 0) {
    return null;
  }

  return (
    <section className="mt-5 grid gap-2.5" aria-label="Mitgliedsausweise">
      {passes.data.map((pass) => (
        <Card key={pass.id} tone="brand">
          <CardHeader>
            <BadgeCheck size={18} aria-hidden="true" />
            <CardTitle>{pass.planName}</CardTitle>
            <CardMeta>{pass.tenantName}</CardMeta>
          </CardHeader>
          <CardBody className="grid justify-items-center gap-2">
            <QrCode value={pass.qr} label={`Mitgliedsausweis ${pass.planName}`} />
            {/* Klartext als Rückfall, wenn ein Scanner streikt. */}
            <p className="num text-[12px] font-bold text-ink-3">{pass.display}</p>
            {pass.validUntil && (
              <p className="text-[12px] text-ink-3">
                gültig bis{" "}
                <span className="num">{new Date(pass.validUntil).toLocaleDateString("de-DE")}</span>
              </p>
            )}
          </CardBody>
        </Card>
      ))}
      <AccessHistory />
    </section>
  );
}

/**
 * Transparenz: was über das Ein- und Auschecken gespeichert wird und wie lange — und
 * die eigene Historie dazu. Der Anbieter sieht davon nur Zahlen.
 */
function AccessHistory(): JSX.Element {
  const [open, setOpen] = useState(false);
  const history = useQuery({
    queryKey: ["my-access"],
    enabled: open,
    queryFn: () => apiRequest<MyAccessDTO[]>("/my/access", { tenant: null }),
  });
  return (
    <div className="grid gap-2">
      <p className="text-[12px] text-ink-3">
        Ein- und Auschecken wird 90 Tage mit deinem Namen gespeichert, danach nur noch als Zahl ohne
        Personenbezug. Der Anbieter sieht Auslastung, nicht deine Besuche.
      </p>
      <Button variant="secondary" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? "Besuche ausblenden" : "Meine Besuche"}
      </Button>
      {open && history.data?.length === 0 && <p className="text-[13px] text-ink-3">Noch keine Besuche.</p>}
      {open && history.data && history.data.length > 0 && (
        <ul className="card divide-y divide-line">
          {history.data.map((entry) => (
            <li key={`${entry.at}-${entry.kind}`} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
              <span className="font-bold text-ink">{entry.kind === "CHECK_IN" ? "Eingecheckt" : "Ausgecheckt"}</span>
              <span className="flex-1 truncate text-ink-2">{entry.tenantName}</span>
              <span className="num text-ink-2">{new Date(entry.at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
